import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetRelevesCorrectionQuery,
  useGenererReleveCorrectionMutation,
  useGetMatieresAvecCopiesQuery,
  useGenererBatchCorrectionMutation,
  useUploadScanCorrectionMutation,
  useLancerOCRCorrectionMutation,
  useGetStatistiquesOCRQuery,
  useDeleteReleveCorrectionMutation,
  ReleveCorrection,
  MatiereAvecCopies,
} from '../../services/ocrApi';
import { useGetMatieresQuery, useGetMatiereEnseignantQuery } from '../../services/matieresApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { useGetOptionsQuery } from '../../services/polesOptionsApi';
import {
  FileText,
  Download,
  Upload,
  Play,
  CheckCircle2,
  XCircle,
  Eye,
  Loader,
  Printer,
  AlertTriangle,
  ClipboardList,
  Trash2,
  MoreVertical,
  History,
  FileSearch,
  ExternalLink,
  RefreshCw,
  Layers,
  Check,
  UserPlus,
} from 'lucide-react';

type NiveauType = 'L1' | 'L2' | 'L3' | 'M1' | 'M2';
// Semestre stocké comme numéro string ('1'-'6'), converti en 'S1'/'S2' pour l'API
type TypeExamenType = 'normal' | 'rattrapage';

const statusLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  genere: { label: 'Généré', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300', icon: <FileText size={14} /> },
  imprime: { label: 'Imprimé', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300', icon: <Printer size={14} /> },
  rempli: { label: 'Rempli', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300', icon: <FileText size={14} /> },
  scanne: { label: 'Scanné', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300', icon: <Upload size={14} /> },
  traite: { label: 'Traité', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300', icon: <Play size={14} /> },
  verifie: { label: 'Vérifié', color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300', icon: <Eye size={14} /> },
  valide: { label: 'Validé', color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300', icon: <CheckCircle2 size={14} /> },
};

const GenerationReleveCorrection: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State pour le formulaire de génération - Ordre: Niveau, Option, Semestre PUIS Matière
  const [selectedNiveau, setSelectedNiveau] = useState<NiveauType | ''>('');
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedSemestre, setSelectedSemestre] = useState<string>('');
  const [selectedMatiere, setSelectedMatiere] = useState<string>('');
  const [typeExamen, setTypeExamen] = useState<TypeExamenType>('normal');
  const [enseignantNom, setEnseignantNom] = useState('');
  const [enseignantPrenoms, setEnseignantPrenoms] = useState('');
  const [enseignantEditable, setEnseignantEditable] = useState(false);

  // State pour l'upload
  const [selectedReleveForUpload, setSelectedReleveForUpload] = useState<ReleveCorrection | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  // State pour les modals
  const [selectedReleveForDetails, setSelectedReleveForDetails] = useState<ReleveCorrection | null>(null);
  const [selectedReleveForDelete, setSelectedReleveForDelete] = useState<ReleveCorrection | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  // Messages
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Parité semestre — sépare relevés/stats des sessions S1/S3/S5 vs S2/S4/S6
  // pour que l'agent reste concentré sur une session à la fois.
  const [semestreParity, setSemestreParity] = useState<'impair' | 'pair'>(
    () => (localStorage.getItem('rc_semestre_parity') as 'impair' | 'pair') || 'impair'
  );
  useEffect(() => {
    localStorage.setItem('rc_semestre_parity', semestreParity);
  }, [semestreParity]);
  const semestreParam = semestreParity === 'pair' ? 'S2' : 'S1';

  // Mode batch
  const [modeBatch, setModeBatch] = useState(false);
  const [batchNiveau, setBatchNiveau] = useState<NiveauType | ''>('');
  const [batchOption, setBatchOption] = useState('');
  const [batchSemestre, setBatchSemestre] = useState('');
  const [batchTypeExamen, setBatchTypeExamen] = useState<TypeExamenType>('normal');
  const [batchSelectedMatieres, setBatchSelectedMatieres] = useState<Set<number>>(new Set());
  const [batchFormateurs, setBatchFormateurs] = useState<Record<string, { nom: string; prenoms: string }>>({});
  const [showFormateurAlert, setShowFormateurAlert] = useState(false);
  const [batchResultat, setBatchResultat] = useState<{
    total_generes: number;
    total_erreurs: number;
    resultats: any[];
    erreurs: string[];
  } | null>(null);

  // Queries et Mutations
  const { data: matieres } = useGetMatieresQuery();
  const { data: options } = useGetOptionsQuery({ actif: true });
  const { data: releves, isLoading: relevesLoading, refetch: refetchReleves } = useGetRelevesCorrectionQuery({ semestre: semestreParam });
  const { data: statistiques } = useGetStatistiquesOCRQuery({ semestre: semestreParam });
  const { data: anneeActive } = useGetAnneeActiveQuery();

  // Récupérer l'enseignant de la matière sélectionnée
  const matiereIdParsed = selectedMatiere ? parseInt(selectedMatiere) : 0;
  const { data: enseignantData } = useGetMatiereEnseignantQuery(
    { matiereId: matiereIdParsed, anneeAcademique: anneeActive?.libelle },
    { skip: !selectedMatiere || !matiereIdParsed || isNaN(matiereIdParsed) || !anneeActive }
  );

  const [genererReleve, { isLoading: genererLoading }] = useGenererReleveCorrectionMutation();
  const [genererBatch, { isLoading: batchLoading }] = useGenererBatchCorrectionMutation();
  const [uploadScan, { isLoading: uploadLoading }] = useUploadScanCorrectionMutation();
  const [lancerOCR, { isLoading: ocrLoading }] = useLancerOCRCorrectionMutation();
  const [deleteReleve, { isLoading: deleteLoading }] = useDeleteReleveCorrectionMutation();

  // Query pour les matières avec copies (mode batch)
  const batchSemestreApi = batchSemestre ? (parseInt(batchSemestre) % 2 === 1 ? 'S1' : 'S2') : '';
  const { data: matieresAvecCopies, isLoading: matieresAvecCopiesLoading } = useGetMatieresAvecCopiesQuery(
    {
      niveau: batchNiveau,
      option: batchOption,
      semestre: batchSemestre,
      annee_academique: anneeActive?.libelle || '',
      type_examen: batchTypeExamen,
    },
    { skip: !batchNiveau || !batchOption || !batchSemestre || !anneeActive }
  );

  // Obtenir les semestres disponibles selon le niveau
  const getSemestresForNiveau = (niveau: string): { value: string; label: string }[] => {
    switch (niveau) {
      case 'L1':
        return [
          { value: '1', label: 'Semestre 1 (S1)' },
          { value: '2', label: 'Semestre 2 (S2)' },
        ];
      case 'L2':
        return [
          { value: '3', label: 'Semestre 3 (S3)' },
          { value: '4', label: 'Semestre 4 (S4)' },
        ];
      case 'L3':
        return [
          { value: '5', label: 'Semestre 5 (S5)' },
          { value: '6', label: 'Semestre 6 (S6)' },
        ];
      // Masters : matières en semestre ABSOLU 7-10 (M1 → S7/S8, M2 → S9/S10).
      // getSemestreForApi les remappe en S1/S2 par parité.
      case 'M1':
        return [
          { value: '7', label: 'Semestre 1 (S1)' },
          { value: '8', label: 'Semestre 2 (S2)' },
        ];
      case 'M2':
        return [
          { value: '9', label: 'Semestre 1 (S1)' },
          { value: '10', label: 'Semestre 2 (S2)' },
        ];
      default:
        return [];
    }
  };

  const semestresDisponibles = useMemo(() => getSemestresForNiveau(selectedNiveau), [selectedNiveau]);

  // Filtrer les matières selon niveau, option et semestre
  const filteredMatieres = useMemo(() => {
    if (!matieres || !selectedNiveau || !selectedOption || !selectedSemestre) {
      return [];
    }
    const semestreNum = parseInt(selectedSemestre);
    return matieres.filter(m => {
      const matchNiveau = m.niveau === selectedNiveau;
      // Comparer l'option de manière flexible (insensible à la casse)
      const matchOption = m.option?.toLowerCase() === selectedOption.toLowerCase();
      const matchSemestre = m.semestre === semestreNum;
      return matchNiveau && matchOption && matchSemestre;
    });
  }, [matieres, selectedNiveau, selectedOption, selectedSemestre]);

  // Mettre à jour l'enseignant quand une matière est sélectionnée
  useEffect(() => {
    if (enseignantData) {
      if (enseignantData.enseignant_nom) {
        setEnseignantNom(enseignantData.enseignant_nom_seul || enseignantData.enseignant_nom);
        setEnseignantPrenoms(enseignantData.enseignant_prenoms || '');
        setEnseignantEditable(false);
      } else {
        setEnseignantNom('');
        setEnseignantPrenoms('');
        setEnseignantEditable(true);
      }
    }
  }, [enseignantData]);

  // Réinitialiser le semestre quand le niveau change
  useEffect(() => {
    setSelectedSemestre('');
  }, [selectedNiveau]);

  // Réinitialiser la matière quand niveau/option/semestre change
  useEffect(() => {
    setSelectedMatiere('');
    setEnseignantNom('');
    setEnseignantPrenoms('');
    setEnseignantEditable(false);
  }, [selectedNiveau, selectedOption, selectedSemestre]);

  // Convertir le semestre numérique en format S1/S2 pour l'API
  const getSemestreForApi = (semestre: string): 'S1' | 'S2' => {
    const num = parseInt(semestre);
    // S1, S3, S5 → S1 (semestres impairs)
    // S2, S4, S6 → S2 (semestres pairs)
    return num % 2 === 1 ? 'S1' : 'S2';
  };

  // Semestres batch
  const batchSemestresDisponibles = useMemo(() => getSemestresForNiveau(batchNiveau), [batchNiveau]);

  // Réinitialiser quand les filtres batch changent
  useEffect(() => {
    setBatchSemestre('');
    setBatchSelectedMatieres(new Set());
    setBatchFormateurs({});
    setShowFormateurAlert(false);
    setBatchResultat(null);
  }, [batchNiveau]);

  useEffect(() => {
    setBatchSelectedMatieres(new Set());
    setBatchFormateurs({});
    setShowFormateurAlert(false);
    setBatchResultat(null);
  }, [batchOption, batchSemestre, batchTypeExamen]);

  // Toggle sélection matière batch
  const toggleBatchMatiere = (matiereId: number) => {
    setBatchSelectedMatieres(prev => {
      const next = new Set(prev);
      if (next.has(matiereId)) {
        next.delete(matiereId);
      } else {
        next.add(matiereId);
      }
      return next;
    });
  };

  // Tout sélectionner / désélectionner (seulement celles sans relevé existant)
  const toggleAllBatchMatieres = () => {
    if (!matieresAvecCopies) return;
    const selectables = matieresAvecCopies.filter(m => !m.releve_existant).map(m => m.matiere_id);
    const allSelected = selectables.every(id => batchSelectedMatieres.has(id));
    if (allSelected) {
      setBatchSelectedMatieres(new Set());
    } else {
      setBatchSelectedMatieres(new Set(selectables));
    }
  };

  // Vérifier les formateurs manquants avant génération
  const matieresAvecFormateurManquant = useMemo(() => {
    if (!matieresAvecCopies) return [];
    return matieresAvecCopies.filter(m =>
      batchSelectedMatieres.has(m.matiere_id) &&
      !m.enseignant &&
      !batchFormateurs[String(m.matiere_id)]?.nom
    );
  }, [matieresAvecCopies, batchSelectedMatieres, batchFormateurs]);

  // Mettre à jour le formateur d'une matière
  const updateBatchFormateur = (matiereId: number, field: 'nom' | 'prenoms', value: string) => {
    setBatchFormateurs(prev => ({
      ...prev,
      [String(matiereId)]: {
        ...prev[String(matiereId)] || { nom: '', prenoms: '' },
        [field]: value,
      },
    }));
  };

  // Générer en batch
  const handleGenererBatch = async () => {
    if (batchSelectedMatieres.size === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez au moins une matière' });
      return;
    }

    // Vérifier formateurs manquants
    if (matieresAvecFormateurManquant.length > 0) {
      setShowFormateurAlert(true);
      setMessage({ type: 'error', text: 'Certaines matières n\'ont pas de formateur. Veuillez compléter les informations ci-dessous.' });
      return;
    }

    // Construire la map des formateurs (inclure ceux qui ont été édités + ceux existants)
    const formateursMap: Record<string, { nom: string; prenoms: string }> = { ...batchFormateurs };

    try {
      const result = await genererBatch({
        matiere_ids: Array.from(batchSelectedMatieres),
        niveau: batchNiveau,
        option: batchOption,
        semestre: batchSemestreApi as 'S1' | 'S2',
        annee_academique: anneeActive!.libelle,
        type_examen: batchTypeExamen,
        formateurs: formateursMap,
      }).unwrap();

      setBatchResultat(result);
      setMessage({
        type: result.total_generes > 0 ? 'success' : 'error',
        text: `${result.total_generes} relevé(s) généré(s)${result.total_erreurs > 0 ? `, ${result.total_erreurs} erreur(s)` : ''}`,
      });

      if (result.total_generes > 0) {
        setBatchSelectedMatieres(new Set());
        refetchReleves();
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la génération batch',
      });
    }
  };

  const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8000' : '';

  // Générer un nouveau relevé
  const handleGenererReleve = async () => {
    if (!selectedMatiere || !selectedNiveau || !selectedOption || !selectedSemestre) {
      setMessage({ type: 'error', text: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }

    if (!anneeActive) {
      setMessage({ type: 'error', text: 'Aucune année académique active' });
      return;
    }

    try {
      const result = await genererReleve({
        matiere_id: parseInt(selectedMatiere),
        niveau: selectedNiveau,
        option: selectedOption,
        semestre: getSemestreForApi(selectedSemestre),
        annee_academique: anneeActive.libelle,
        type_examen: typeExamen,
        enseignant_nom: enseignantNom || undefined,
        enseignant_prenoms: enseignantPrenoms || undefined,
      }).unwrap();

      setMessage({
        type: 'success',
        text: `Relevé ${result.code_releve} généré avec succès (${result.nombre_copies} copies, ${result.nombre_pages} pages)`,
      });

      // Réinitialiser le formulaire
      setSelectedMatiere('');
      setSelectedNiveau('');
      setSelectedOption('');
      setSelectedSemestre('');
      setEnseignantNom('');
      setEnseignantPrenoms('');
      setEnseignantEditable(false);

      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la génération du relevé',
      });
    }
  };

  // Upload du scan
  const handleUploadScan = async () => {
    if (!selectedReleveForUpload || !uploadFile) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un relevé et un fichier' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('fichier', uploadFile);

      await uploadScan({
        releveId: selectedReleveForUpload.id,
        formData,
      }).unwrap();

      setMessage({ type: 'success', text: 'Fichier uploadé avec succès' });
      setSelectedReleveForUpload(null);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de l\'upload du fichier',
      });
    }
  };

  // Lancer le traitement OCR
  const handleLancerOCR = async (releve: ReleveCorrection) => {
    try {
      const result = await lancerOCR(releve.id).unwrap();
      setMessage({
        type: 'success',
        text: `Traitement OCR lancé pour ${releve.code_releve}. ID tâche: ${result.task_id}`,
      });
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors du lancement du traitement OCR',
      });
    }
  };

  // Headers communs pour les appels fetch directs
  const getFetchHeaders = () => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    return headers;
  };

  // Télécharger le PDF
  const handleDownloadPDF = async (releve: ReleveCorrection) => {
    try {
      const response = await fetch(`${baseUrl}/api/ocr/releves-correction/${releve.id}/telecharger_pdf/`, {
        headers: getFetchHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${releve.code_releve}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setMessage({
          type: 'error',
          text: 'Erreur lors du téléchargement du PDF',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Erreur lors du téléchargement du PDF',
      });
    }
  };

  // Aperçu du PDF
  const handlePreviewPDF = async (releve: ReleveCorrection) => {
    try {
      const response = await fetch(`${baseUrl}/api/ocr/releves-correction/${releve.id}/telecharger_pdf/`, {
        headers: getFetchHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setMessage({
          type: 'error',
          text: 'Erreur lors de l\'aperçu du PDF',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Erreur lors de l\'aperçu du PDF',
      });
    }
  };

  // Supprimer un relevé
  const handleDeleteReleve = async () => {
    if (!selectedReleveForDelete) return;

    try {
      await deleteReleve(selectedReleveForDelete.id).unwrap();
      setMessage({ type: 'success', text: `Relevé ${selectedReleveForDelete.code_releve} supprimé avec succès` });
      setSelectedReleveForDelete(null);
      refetchReleves();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.data?.error || 'Erreur lors de la suppression du relevé',
      });
    }
  };

  // Aller à la vérification
  const handleVerifier = (releve: ReleveCorrection) => {
    navigate(`/ocr/verification-correction/${releve.id}`);
  };

  // Formater la date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 dark:bg-gray-900 min-h-screen">
      {/* En-tête */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
            <ClipboardList className="mr-3" size={32} />
            Relevés de Correction - OCR
            <span className={`ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
              semestreParity === 'pair'
                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
            }`}>
              {semestreParity === 'pair' ? 'Semestres pairs (S2/S4/S6)' : 'Semestres impairs (S1/S3/S5)'}
            </span>
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Générez des relevés de correction d'examens avec cases à cocher, uploadez les scans et traitez-les par OCR
          </p>
          <div className="mt-2 inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setSemestreParity('impair')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                semestreParity === 'impair'
                  ? 'bg-emerald-600 text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Affiche uniquement les relevés et stats des semestres impairs"
            >
              S1 / S3 / S5 (impairs)
            </button>
            <button
              onClick={() => setSemestreParity('pair')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                semestreParity === 'pair'
                  ? 'bg-purple-600 text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Affiche uniquement les relevés et stats des semestres pairs"
            >
              S2 / S4 / S6 (pairs)
            </button>
          </div>
        </div>
        <button
          onClick={() => refetchReleves()}
          className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          title="Actualiser la liste"
        >
          <RefreshCw size={16} className="mr-2" />
          Actualiser
        </button>
      </div>

      {/* Message de feedback */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-center ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="mr-2 flex-shrink-0" size={20} />
          ) : (
            <XCircle className="mr-2 flex-shrink-0" size={20} />
          )}
          {message.text}
          <button
            onClick={() => setMessage(null)}
            className="ml-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XCircle size={18} />
          </button>
        </div>
      )}

      {/* Statistiques */}
      {statistiques && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Relevés Correction</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
              {statistiques.total_releves_correction}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">En attente</div>
            <div className="text-2xl font-bold text-orange-600 mt-1">
              {statistiques.releves_en_attente}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">À vérifier</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">
              {statistiques.releves_a_verifier}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Validés</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {statistiques.releves_valides}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Confiance moy.</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">
              {(statistiques.confiance_moyenne * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulaire de génération */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            {/* Onglets Unitaire / Batch */}
            <div className="flex mb-6 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setModeBatch(false)}
                className={`flex-1 py-2 text-center text-sm font-medium border-b-2 transition-colors ${
                  !modeBatch
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <FileText size={16} className="inline mr-1" />
                Unitaire
              </button>
              <button
                onClick={() => setModeBatch(true)}
                className={`flex-1 py-2 text-center text-sm font-medium border-b-2 transition-colors ${
                  modeBatch
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Layers size={16} className="inline mr-1" />
                Batch
              </button>
            </div>

            {!modeBatch ? (
            /* ==================== MODE UNITAIRE ==================== */
            <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
              Générer un nouveau relevé
            </h2>

            <div className="space-y-4">
              {/* Niveau - Premier champ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Niveau <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedNiveau}
                  onChange={(e) => setSelectedNiveau(e.target.value as NiveauType)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="L1">L1</option>
                  <option value="L2">L2</option>
                  <option value="L3">L3</option>
                  <option value="M1">M1</option>
                  <option value="M2">M2</option>
                </select>
              </div>

              {/* Option - Deuxième champ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Option <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  {Array.isArray(options) &&
                    options.map((option) => (
                      <option key={option.id} value={option.code}>
                        {option.nom}
                      </option>
                    ))}
                </select>
              </div>

              {/* Semestre - Troisième champ (dynamique selon le niveau) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Semestre <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedSemestre}
                  onChange={(e) => setSelectedSemestre(e.target.value)}
                  disabled={!selectedNiveau}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!selectedNiveau ? '-- Sélectionnez d\'abord un niveau --' : '-- Sélectionner --'}
                  </option>
                  {semestresDisponibles.map((sem) => (
                    <option key={sem.value} value={sem.value}>
                      {sem.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Matière - Quatrième champ, filtré selon niveau/option/semestre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Matière <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedMatiere}
                  onChange={(e) => setSelectedMatiere(e.target.value)}
                  disabled={!selectedNiveau || !selectedOption || !selectedSemestre}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!selectedNiveau || !selectedOption || !selectedSemestre
                      ? '-- Sélectionnez d\'abord niveau, option et semestre --'
                      : filteredMatieres.length === 0
                      ? '-- Aucune matière trouvée --'
                      : '-- Sélectionner --'}
                  </option>
                  {filteredMatieres.map((matiere) => (
                    <option key={matiere.id} value={matiere.id}>
                      {matiere.code_matiere} - {matiere.nom_matiere}
                    </option>
                  ))}
                </select>
                {selectedNiveau && selectedOption && selectedSemestre && filteredMatieres.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Aucune matière pour cette combinaison niveau/option/semestre
                  </p>
                )}
              </div>

              {/* Enseignant - Auto-rempli ou éditable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enseignant
                  {!enseignantEditable && enseignantNom && (
                    <span className="text-green-600 text-xs ml-2">(auto)</span>
                  )}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={enseignantNom}
                    onChange={(e) => setEnseignantNom(e.target.value)}
                    disabled={!enseignantEditable && !!enseignantNom}
                    placeholder={selectedMatiere ? "Nom" : "Sélectionnez une matière"}
                    className={`flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${
                      !enseignantEditable && enseignantNom ? 'bg-gray-50 dark:bg-gray-600' : ''
                    }`}
                  />
                  <input
                    type="text"
                    value={enseignantPrenoms}
                    onChange={(e) => setEnseignantPrenoms(e.target.value)}
                    disabled={!enseignantEditable && !!enseignantNom}
                    placeholder="Prénoms"
                    className={`flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${
                      !enseignantEditable && enseignantNom ? 'bg-gray-50 dark:bg-gray-600' : ''
                    }`}
                  />
                  {enseignantNom && !enseignantEditable && (
                    <button
                      type="button"
                      onClick={() => setEnseignantEditable(true)}
                      className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                      title="Modifier l'enseignant"
                    >
                      Modifier
                    </button>
                  )}
                </div>
                {!enseignantNom && selectedMatiere && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Aucun enseignant assigné à cette matière. Vous pouvez saisir un nom.
                  </p>
                )}
              </div>

              {/* Type d'examen */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type d'examen
                </label>
                <select
                  value={typeExamen}
                  onChange={(e) => setTypeExamen(e.target.value as TypeExamenType)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="normal">Session ordinaire</option>
                  <option value="rattrapage">Rattrapage</option>
                </select>
              </div>

              {/* Année académique - Affichage informatif seulement */}
              {anneeActive && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <span className="font-medium">Année académique :</span> {anneeActive.libelle}
                  </p>
                </div>
              )}

              <button
                onClick={handleGenererReleve}
                disabled={genererLoading || !selectedMatiere || !selectedNiveau || !selectedOption || !selectedSemestre}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {genererLoading ? (
                  <Loader className="animate-spin mr-2" size={20} />
                ) : (
                  <FileText className="mr-2" size={20} />
                )}
                Générer le relevé
              </button>
            </div>
            </div>
            ) : (
            /* ==================== MODE BATCH ==================== */
            <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Génération en batch
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Sélectionnez les matières avec copies associées pour générer plusieurs relevés simultanément.
            </p>

            <div className="space-y-4">
              {/* Filtres batch: Niveau, Option, Semestre */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau *</label>
                  <select
                    value={batchNiveau}
                    onChange={(e) => setBatchNiveau(e.target.value as NiveauType)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">--</option>
                    <option value="L1">L1</option>
                    <option value="L2">L2</option>
                    <option value="L3">L3</option>
                    <option value="M1">M1</option>
                    <option value="M2">M2</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type examen</label>
                  <select
                    value={batchTypeExamen}
                    onChange={(e) => setBatchTypeExamen(e.target.value as TypeExamenType)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="normal">Normal</option>
                    <option value="rattrapage">Rattrapage</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Option *</label>
                <select
                  value={batchOption}
                  onChange={(e) => setBatchOption(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  {Array.isArray(options) && options.map((opt) => (
                    <option key={opt.id} value={opt.code}>{opt.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre *</label>
                <select
                  value={batchSemestre}
                  onChange={(e) => setBatchSemestre(e.target.value)}
                  disabled={!batchNiveau}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600"
                >
                  <option value="">{!batchNiveau ? '-- Niveau requis --' : '-- Sélectionner --'}</option>
                  {batchSemestresDisponibles.map((sem) => (
                    <option key={sem.value} value={sem.value}>{sem.label}</option>
                  ))}
                </select>
              </div>

              {anneeActive && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-2">
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    <span className="font-medium">Année :</span> {anneeActive.libelle}
                  </p>
                </div>
              )}

              {/* Liste des matières avec copies */}
              {batchNiveau && batchOption && batchSemestre && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Matières avec copies associées
                    </label>
                    {matieresAvecCopies && matieresAvecCopies.filter(m => !m.releve_existant).length > 0 && (
                      <button
                        onClick={toggleAllBatchMatieres}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {matieresAvecCopies.filter(m => !m.releve_existant).every(m => batchSelectedMatieres.has(m.matiere_id))
                          ? 'Tout désélectionner'
                          : 'Tout sélectionner'}
                      </button>
                    )}
                  </div>

                  {matieresAvecCopiesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader className="animate-spin text-blue-600" size={20} />
                      <span className="ml-2 text-sm text-gray-500">Chargement...</span>
                    </div>
                  ) : !matieresAvecCopies || matieresAvecCopies.length === 0 ? (
                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                      Aucune matière avec copies associées
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-100 dark:divide-gray-700">
                      {matieresAvecCopies.map((m) => {
                        const isDisabled = !!m.releve_existant;
                        const isSelected = batchSelectedMatieres.has(m.matiere_id);
                        const hasFormateur = !!m.enseignant || !!batchFormateurs[String(m.matiere_id)]?.nom;
                        return (
                          <div
                            key={m.matiere_id}
                            className={`flex items-center px-3 py-2 text-sm ${
                              isDisabled
                                ? 'bg-gray-50 dark:bg-gray-700/30 opacity-60'
                                : isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={() => toggleBatchMatiere(m.matiere_id)}
                              className="mr-3 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                {m.code_matiere} - {m.nom_matiere}
                              </div>
                              <div className="flex items-center space-x-2 mt-0.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {m.copies_associees} copie(s)
                                </span>
                                {m.enseignant ? (
                                  <span className="text-xs text-green-600 dark:text-green-400">
                                    {m.enseignant.nom_complet}
                                  </span>
                                ) : batchFormateurs[String(m.matiere_id)]?.nom ? (
                                  <span className="text-xs text-blue-600 dark:text-blue-400">
                                    {batchFormateurs[String(m.matiere_id)].nom} {batchFormateurs[String(m.matiere_id)].prenoms}
                                  </span>
                                ) : (
                                  <span className="text-xs text-orange-500 flex items-center">
                                    <AlertTriangle size={10} className="mr-0.5" />
                                    Sans formateur
                                  </span>
                                )}
                                {isDisabled && m.releve_existant && (
                                  <span className="text-xs text-purple-600 dark:text-purple-400">
                                    Relevé existant
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {batchSelectedMatieres.size > 0 && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {batchSelectedMatieres.size} matière(s) sélectionnée(s)
                    </p>
                  )}
                </div>
              )}

              {/* Alerte formateurs manquants */}
              {showFormateurAlert && matieresAvecFormateurManquant.length > 0 && (
                <div className="border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <UserPlus size={18} className="text-orange-600 mr-2" />
                    <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">
                      Formateurs manquants ({matieresAvecFormateurManquant.length})
                    </h4>
                  </div>
                  <p className="text-xs text-orange-700 dark:text-orange-400 mb-3">
                    Complétez les informations ci-dessous pour continuer. Les formateurs seront automatiquement enregistrés.
                  </p>
                  <div className="space-y-3">
                    {matieresAvecFormateurManquant.map((m) => (
                      <div key={m.matiere_id} className="bg-white dark:bg-gray-800 rounded-md p-3 border border-orange-200 dark:border-orange-800">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {m.code_matiere} - {m.nom_matiere}
                        </div>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={batchFormateurs[String(m.matiere_id)]?.nom || ''}
                            onChange={(e) => updateBatchFormateur(m.matiere_id, 'nom', e.target.value)}
                            placeholder="Nom *"
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                          />
                          <input
                            type="text"
                            value={batchFormateurs[String(m.matiere_id)]?.prenoms || ''}
                            onChange={(e) => updateBatchFormateur(m.matiere_id, 'prenoms', e.target.value)}
                            placeholder="Prénoms"
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bouton génération batch */}
              <button
                onClick={handleGenererBatch}
                disabled={batchLoading || batchSelectedMatieres.size === 0 || !batchNiveau || !batchOption || !batchSemestre}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {batchLoading ? (
                  <Loader className="animate-spin mr-2" size={20} />
                ) : (
                  <Layers className="mr-2" size={20} />
                )}
                Générer {batchSelectedMatieres.size} relevé(s)
              </button>

              {/* Résultats batch */}
              {batchResultat && (
                <div className={`rounded-lg p-4 ${
                  batchResultat.total_generes > 0
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center mb-2">
                    {batchResultat.total_generes > 0 ? (
                      <CheckCircle2 size={18} className="text-green-600 mr-2" />
                    ) : (
                      <XCircle size={18} className="text-red-600 mr-2" />
                    )}
                    <span className="font-medium text-sm dark:text-gray-200">
                      {batchResultat.total_generes} généré(s), {batchResultat.total_erreurs} erreur(s)
                    </span>
                  </div>
                  {batchResultat.resultats.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {batchResultat.resultats.map((r: any, i: number) => (
                        <div key={i} className="text-xs text-green-700 dark:text-green-400 flex items-center">
                          <Check size={12} className="mr-1 flex-shrink-0" />
                          {r.matiere_code} - {r.nombre_copies} copies
                          {r.enseignant && <span className="ml-1 text-gray-500">({r.enseignant})</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {batchResultat.erreurs.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {batchResultat.erreurs.map((err: string, i: number) => (
                        <div key={i} className="text-xs text-red-600 dark:text-red-400 flex items-center">
                          <XCircle size={12} className="mr-1 flex-shrink-0" />
                          {err}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-4">Instructions</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800 dark:text-blue-300">
              <li>Assurez-vous que les copies sont associées aux étudiants</li>
              <li>Générez un relevé de correction avec le formulaire</li>
              <li>Téléchargez et imprimez le relevé (format cases à cocher)</li>
              <li>Le correcteur coche la note et écrit la note manuscrite</li>
              <li>Scannez le relevé rempli (300 DPI, couleur)</li>
              <li>Uploadez le scan et lancez le traitement OCR</li>
              <li>Vérifiez les notes détectées (cases vs manuscrit)</li>
            </ol>

            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 rounded">
              <p className="text-xs text-yellow-800 dark:text-yellow-300 font-medium">
                Le système vérifie la cohérence entre les cases cochées et la note manuscrite.
                En cas de différence, une vérification manuelle est demandée.
              </p>
            </div>
          </div>
        </div>

        {/* Liste des relevés */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Relevés existants ({Array.isArray(releves) ? releves.length : 0})
              </h2>
            </div>

            {relevesLoading ? (
              <div className="p-8 text-center">
                <Loader className="animate-spin mx-auto text-blue-600" size={32} />
                <p className="mt-2 text-gray-600 dark:text-gray-400">Chargement...</p>
              </div>
            ) : !releves || !Array.isArray(releves) || releves.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                Aucun relevé trouvé. Générez votre premier relevé de correction.
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {releves.map((releve) => (
                  <div key={releve.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {releve.code_releve}
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              statusLabels[releve.status]?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            {statusLabels[releve.status]?.icon}
                            <span className="ml-1">{statusLabels[releve.status]?.label || releve.status}</span>
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              releve.type_examen === 'rattrapage'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {releve.type_examen === 'rattrapage' ? 'Rattrapage' : 'Normal'}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {releve.matiere_code} - {releve.matiere_nom}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {releve.niveau} | {releve.classes || releve.option} | {releve.semestre} | {releve.annee_academique}
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <span>{releve.nombre_copies} copies</span>
                          <span>{releve.nombre_pages} pages</span>
                          <span>Créé le {formatDate(releve.date_generation)}</span>
                          {releve.copies_manquantes > 0 && (
                            <span className="text-red-600 flex items-center">
                              <AlertTriangle size={12} className="mr-1" />
                              {releve.copies_manquantes} manquantes
                            </span>
                          )}
                          {releve.lignes_a_verifier > 0 && (
                            <span className="text-orange-600 flex items-center">
                              <AlertTriangle size={12} className="mr-1" />
                              {releve.lignes_a_verifier} à vérifier
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {/* Boutons d'actions principaux */}
                        <div className="flex flex-col space-y-1">
                          {/* Télécharger PDF */}
                          <button
                            onClick={() => handleDownloadPDF(releve)}
                            className="flex items-center px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                            title="Télécharger le PDF"
                          >
                            <Download size={16} className="mr-1" />
                            Télécharger
                          </button>

                          {/* Aperçu */}
                          <button
                            onClick={() => handlePreviewPDF(releve)}
                            className="flex items-center px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                            title="Aperçu du PDF"
                          >
                            <ExternalLink size={16} className="mr-1" />
                            Aperçu
                          </button>
                        </div>

                        {/* Actions selon le statut */}
                        <div className="flex flex-col space-y-1">
                          {/* Upload Scan */}
                          {['genere', 'imprime', 'rempli'].includes(releve.status) && (
                            <button
                              onClick={() => setSelectedReleveForUpload(releve)}
                              className="flex items-center px-3 py-1.5 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50"
                              title="Uploader le scan"
                            >
                              <Upload size={16} className="mr-1" />
                              Upload
                            </button>
                          )}

                          {/* Lancer OCR */}
                          {releve.status === 'scanne' && (
                            <button
                              onClick={() => handleLancerOCR(releve)}
                              disabled={ocrLoading}
                              className="flex items-center px-3 py-1.5 text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-200 dark:hover:bg-indigo-900/50 disabled:opacity-50"
                              title="Lancer le traitement OCR"
                            >
                              {ocrLoading ? (
                                <Loader size={16} className="mr-1 animate-spin" />
                              ) : (
                                <Play size={16} className="mr-1" />
                              )}
                              OCR
                            </button>
                          )}

                          {/* Vérifier */}
                          {['traite', 'verifie'].includes(releve.status) && (
                            <button
                              onClick={() => handleVerifier(releve)}
                              className="flex items-center px-3 py-1.5 text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded hover:bg-yellow-200 dark:hover:bg-yellow-900/50"
                              title="Vérifier les notes"
                            >
                              <Eye size={16} className="mr-1" />
                              Vérifier
                            </button>
                          )}

                          {/* Validé */}
                          {releve.status === 'valide' && (
                            <span className="flex items-center px-3 py-1.5 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                              <CheckCircle2 size={16} className="mr-1" />
                              Validé
                            </span>
                          )}
                        </div>

                        {/* Menu contextuel */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === releve.id ? null : releve.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openMenuId === releve.id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                              <button
                                onClick={() => {
                                  setSelectedReleveForDetails(releve);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <FileSearch size={16} className="mr-2" />
                                Détails
                              </button>
                              <button
                                onClick={() => {
                                  navigate(`/ocr/historique-correction/${releve.id}`);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <History size={16} className="mr-2" />
                                Historique
                              </button>
                              <hr className="my-1" />
                              <button
                                onClick={() => {
                                  setSelectedReleveForDelete(releve);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 size={16} className="mr-2" />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Upload Scan */}
      {selectedReleveForUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Uploader le scan - {selectedReleveForUpload.code_releve}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fichier scanné (PDF ou images)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Format recommandé: PDF multi-pages ou images à 300 DPI en couleur
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleUploadScan}
                disabled={uploadLoading || !uploadFile}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadLoading ? (
                  <Loader className="animate-spin mr-2" size={18} />
                ) : (
                  <Upload className="mr-2" size={18} />
                )}
                Uploader
              </button>
              <button
                onClick={() => {
                  setSelectedReleveForUpload(null);
                  setUploadFile(null);
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {selectedReleveForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Détails du relevé - {selectedReleveForDetails.code_releve}
              </h3>
              <button
                onClick={() => setSelectedReleveForDetails(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Matière</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.matiere_code} - {selectedReleveForDetails.matiere_nom}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Niveau / Classe(s)</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.niveau} - {selectedReleveForDetails.classes || selectedReleveForDetails.option}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Type d'examen</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.type_examen === 'rattrapage' ? 'Rattrapage' : 'Session ordinaire'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Année académique</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.annee_academique}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Statut</span>
                  <p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabels[selectedReleveForDetails.status]?.color}`}>
                      {statusLabels[selectedReleveForDetails.status]?.label}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nombre de copies</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.nombre_copies}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Nombre de pages</span>
                  <p className="font-medium dark:text-gray-200">{selectedReleveForDetails.nombre_pages}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Date de génération</span>
                  <p className="font-medium dark:text-gray-200">{formatDate(selectedReleveForDetails.date_generation)}</p>
                </div>
              </div>
            </div>

            {/* Statistiques OCR si traité */}
            {['traite', 'verifie', 'valide'].includes(selectedReleveForDetails.status) && (
              <div className="mt-6 pt-4 border-t dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Résultats OCR</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Notes détectées</span>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{selectedReleveForDetails.total_notes_detectees}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Confiance haute</span>
                    <p className="text-xl font-bold text-green-600">{selectedReleveForDetails.notes_confiance_haute}</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded">
                    <span className="text-sm text-gray-500 dark:text-gray-400">À vérifier</span>
                    <p className="text-xl font-bold text-orange-600">{selectedReleveForDetails.notes_a_verifier}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Copies manquantes</span>
                    <p className="text-xl font-bold text-red-600">{selectedReleveForDetails.copies_manquantes}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => handleDownloadPDF(selectedReleveForDetails)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Download size={18} className="mr-2" />
                Télécharger PDF
              </button>
              <button
                onClick={() => setSelectedReleveForDetails(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {selectedReleveForDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Confirmer la suppression
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Cette action est irréversible
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Êtes-vous sûr de vouloir supprimer le relevé <strong>{selectedReleveForDelete.code_releve}</strong> ?
              Tous les fichiers et données associés seront définitivement supprimés.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleDeleteReleve}
                disabled={deleteLoading}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? (
                  <Loader className="animate-spin mr-2" size={18} />
                ) : (
                  <Trash2 className="mr-2" size={18} />
                )}
                Supprimer
              </button>
              <button
                onClick={() => setSelectedReleveForDelete(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fermer menu contextuel en cliquant ailleurs */}
      {openMenuId && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setOpenMenuId(null)}
        />
      )}
    </div>
  );
};

export default GenerationReleveCorrection;
